<template>
  <div>
    <Card class="w-full">
      <template #title>Project Statuses</template>
      <template #content>
        <div class="flex flex-col w-full h-[70vh]">
          <ag-grid-vue
            theme="legacy"
            :rowData="projectStatuses"
            :immutableData="true"
            :getRowId="(params: GetRowIdParams) => params.data.projectStatusId"
            :columnDefs="colDefs"
            :defaultColDef="defaultColDefs"
            :autoSizeStrategy="{ type: 'fitGridWidth' }"
            :suppressCellFocus="true"
            class="mx-4 h-full"
            :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
            :suppressDragLeaveHidesColumns="true"
            :pinnedBottomRowData="[{}]"
            :isFullWidthRow="(params: IsFullWidthRowParams) => params.rowNode.rowPinned === 'bottom'"
            :fullWidthCellRenderer="CustomAddNewCellRenderer"
            :fullWidthCellRendererParams="{
              callback: openAddModal,
              label: 'status',
              display: true,
            }"
            @row-data-updated="resize"
            @grid-size-changed="resize"
          >
          </ag-grid-vue>
        </div>
      </template>
    </Card>
    <TSDialog label="Add Flex Row Template" v-model:visible="modalMode" :icon="modalMode == 'Add' ? 'plus' : 'pencil'" size="w-[500px]!">
      <template #content>
        <div class="flex flex-col gap-3">
          <StackedLabelInputNoForm v-model="statusName" name="statusName" label="Name"></StackedLabelInputNoForm>
          <StackedLabelDropdownNoForm
            v-model="selectedStatusCategory"
            name="projectStatus"
            label="Status"
            :options="ProjectStatusCategoryValues"
            options-label=""
          />
          <Button :label="modalMode == 'Add' ? 'Add' : 'Save'" icon="pi pi-check" iconPos="right" class="mt-3" @click="statusModalSuccess"></Button>
          <Button label="Cancel" icon="pi pi-times" iconPos="right" severity="surface" @click="closeStatusModal"></Button>
        </div>
      </template>
    </TSDialog>
  </div>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { usePropsStore } from "@/stores/props";
import type { GetRowIdParams, GridSizeChangedEvent, IsFullWidthRowParams, RowDataUpdatedEvent, ValueGetterParams } from "ag-grid-community";
import { getProjectStatuses } from "@/services/Utility";
import type { ProjectStatus } from "@/types/ProjectStatus";
import CustomTagRenderer from "../cell-renderers/CustomTagRenderer.vue";
import { CustomTagType } from "@/enums/CustomTagType";
import { getStatusFromString, ProjectStatusCategory, ProjectStatusCategoryValues } from "@/enums/ProjectStatusCategory";
import CustomAddNewCellRenderer from "../cell-renderers/CustomAddNewCellRenderer.vue";
import CustomEditIconCellRenderer from "../cell-renderers/CustomEditIconCellRenderer.vue";
import CustomDeleteIconCellRenderer from "../cell-renderers/CustomDeleteIconCellRenderer.vue";

const propsStore = usePropsStore();
const darkVal = computed<boolean>(() => propsStore.darkMode);

const defaultStatuses = ref<ProjectStatus[]>([]);
const projectStatuses = ref<ProjectStatus[]>([]);

const modalMode = ref<"Add" | "Edit" | null>(null);
const statusName = ref<string>("");
const selectedStatusCategory = ref<string>("");

const editingStatus = ref<ProjectStatus | null>(null);

const openAddModal = (): void => {
  editingStatus.value = null;
  statusName.value = "";
  selectedStatusCategory.value = "";
  modalMode.value = "Add";
};

const openEditModal = (status: ProjectStatus): void => {
  editingStatus.value = status;
  statusName.value = status.name;
  selectedStatusCategory.value = ProjectStatusCategoryValues[status.category];
  modalMode.value = "Edit";
};

const refreshStatuses = (): void => {
  getProjectStatuses().then((result: ProjectStatus[]) => {
    projectStatuses.value = result;
    defaultStatuses.value = result.filter((status) => {
      return ProjectStatusCategoryValues.includes(status.name);
    });
  });
};

const checkValidStatus = (): ProjectStatus | null => {
  if (statusName.value === "") {
    error("Invalid Project Status", "The project status name may not be blank");
    return null;
  }

  let existingStatus: boolean = false;
  projectStatuses.value.forEach((status) => {
    if (status.name.toUpperCase() === statusName.value.toUpperCase()) {
      if (editingStatus.value == null || status.projectStatusId !== editingStatus.value.projectStatusId) existingStatus = true;
    }
  });

  if (existingStatus) {
    error("Invalid Project Status", "A project status with that name already exists");
    return null;
  }

  const category: ProjectStatusCategory | null = getStatusFromString(selectedStatusCategory.value);
  if (category == null) {
    error("Invalid Project Status", "You must select a project status category");
    return null;
  }

  return {
    projectStatusId: editingStatus.value != null ? editingStatus.value.projectStatusId : 0,
    category: category,
    name: statusName.value,
  };
};

const addNewStatus = (): void => {
  const newStatus = checkValidStatus();
  if (newStatus == null) return;

  axios.post("/project-status/add", newStatus).then((response) => {
    handleApiResponse(
      response,
      (data: any) => {
        refreshStatuses();
        success("Project status added!", "");
        closeStatusModal();
      },
      (errorlist: ApiError[]) => {
        for (let e of errorlist) {
          error("An Error Occurred While Adding Status", e.message);
        }
      }
    );
  });
};

const updateStatus = (): void => {
  const updatedStatus = checkValidStatus();
  if (updatedStatus == null) return;

  axios.post("/project-status/update", updatedStatus).then((response) => {
    handleApiResponse(
      response,
      (data: any) => {
        refreshStatuses();
        success("Project status updated!", "");
        closeStatusModal();
      },
      (errorlist: ApiError[]) => {
        for (let e of errorlist) {
          error("An Error Occurred While Adding Status", e.message);
        }
      }
    );
  });
};

const deleteStatus = (status: ProjectStatus): void => {
  axios.delete("/project-status/delete/" + status.projectStatusId).then((response) => {
    handleApiResponse(
      response,
      (data: any) => {
        projectStatuses.value = projectStatuses.value.filter((s) => s.projectStatusId !== status.projectStatusId);
        success("Project status deleted!", "");
      },
      (errorlist: ApiError[]) => {
        for (let e of errorlist) {
          error("An Error Occurred While Adding Status", e.message);
        }
      }
    );
  });
};

const closeStatusModal = (): void => {
  modalMode.value = null;
};

const statusModalSuccess = (): void => {
  if (modalMode.value === "Add") addNewStatus();
  else updateStatus();
};

const resize = (event: GridSizeChangedEvent | RowDataUpdatedEvent): void => {
  event.api.sizeColumnsToFit();
};

const defaultColDefs = {
  cellDataType: "text",
  editable: false,
  wrapHeaderText: true,
};

const colDefs = ref([
  {
    field: "delete",
    headerName: "",
    cellClass: "text-center",
    cellRenderer: CustomDeleteIconCellRenderer,
    cellRendererParams: {
      deleteConfirmMessage: (projectStatus: ProjectStatus) => `Are you sure you want to delete this project status "${projectStatus.name}"?`,
      deleteConfirmHeader: () => "Delete Project Status",
      callback: deleteStatus,
      checkDeletable: (projectStatus: ProjectStatus) => !ProjectStatusCategoryValues.includes(projectStatus.name),
    },
    width: 75,
    maxWidth: 75,
  },
  {
    field: "edit",
    headerName: "",
    cellClass: "text-center",
    cellRenderer: CustomEditIconCellRenderer,
    cellRendererParams: {
      callback: openEditModal,
      checkEditable: (projectStatus: ProjectStatus) => !ProjectStatusCategoryValues.includes(projectStatus.name),
    },
    width: 75,
    maxWidth: 75,
  },
  {
    field: "category",
    headerName: "Category",
    cellDataType: "number",
    headerClass: "grid-align-center",
    cellClass: "text-center",
    valueGetter: (params: ValueGetterParams) => ProjectStatusCategoryValues[params.data.category],
    cellRenderer: CustomTagRenderer,
    cellRendererParams: {
      type: CustomTagType.PROJECT_STATUS_CATEGORY,
    },
    maxWidth: 200,
  },
  {
    field: "name",
    headerName: "Project Status Name",
    headerClass: "",
  },
]);

onBeforeMount(async (): Promise<void> => {
  refreshStatuses();
});
</script>
